import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import TopNav from '../../components/TopNav'
import CustomerAvatar from '../../components/CustomerAvatar'
import {
  getCustomer, updateCustomer, getTerminology, getPlatformConfig, previewDeleteCustomer, executeDeleteCustomer,
  getContextHistory, getContextSuggestions, approveContextSuggestion, rejectContextSuggestion,
} from '../../api'
import './CustomerEdit.css'
import { DIRECT, contractedThroughOptions, isResold, providerLabel } from '../../utils/providedThrough'
import { changedFields, savePlan, serverRecordedChange, SERVER_NO_CHANGE } from '../../utils/contextEdit'

const TABS = [
  { id: 'overview', label: 'Overview', icon: '🏢' },
  { id: 'systems', label: 'Tech Landscape', icon: '⚙️' },
  { id: 'contacts', label: 'Contacts', icon: '👥' },
  { id: 'terminology', label: 'Terminology', icon: '📖' },
  { id: 'populations', label: 'Populations', icon: '🏷️' },
  { id: 'data', label: 'Data & Pay', icon: '💰' },
  { id: 'delivery', label: 'Delivery', icon: '📤' },
  { id: 'suggestions', label: 'Suggestions', icon: '💬' },
  { id: 'history', label: 'History', icon: '🕘' },
]

// Fields that never leave Sempirical (Scott, 2026-10-05). The server keeps them in a
// separate record a customer is never served; this badge says so on the form.
function InternalBadge() {
  return (
    <span className="ce-internal-badge" title="Stored apart from the customer-facing record; never shown to the customer">
      Internal — never shown to the customer
    </span>
  )
}

function fmtWhen(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  return isNaN(d) ? iso : d.toLocaleString()
}

function fmtValue(v) {
  if (v === null || v === undefined || v === '') return '—'
  if (typeof v === 'string') return v
  return JSON.stringify(v)
}

// Color palette for domains — cycles if more domains are added
const DOMAIN_COLOR_PALETTE = [
  { bg: '#eef2fb', border: '#2d5bc0', text: '#1e40af' },
  { bg: '#faf5ff', border: '#7c3aed', text: '#5b21b6' },
  { bg: '#f6ebd6', border: '#b5791e', text: '#92400e' },
  { bg: '#ecfdf5', border: '#059669', text: '#065f46' },
  { bg: '#fff1f2', border: '#e11d48', text: '#9f1239' },
  { bg: '#f0f9ff', border: '#0284c7', text: '#075985' },
  { bg: '#fefce8', border: '#ca8a04', text: '#854d0e' },
  { bg: '#fdf4ff', border: '#c026d3', text: '#86198f' },
]

const CONTACT_AVATAR_COLORS = ['#2d5bc0', '#7c3aed', '#059669', '#b5791e', '#e11d48', '#0284c7', '#b45309', '#4338ca']

// Fallback values if platform-config hasn't loaded yet
const FALLBACK_DOMAINS = [{ id: 'hcm', label: 'HCM' }]
const FALLBACK_STATUSES = [
  { id: 'active', label: 'Active', color: 'green' },
  { id: 'planned', label: 'Planned', color: 'blue' },
  { id: 'decommissioning', label: 'Decommissioning', color: 'amber' },
  { id: 'retired', label: 'Retired', color: 'gray' },
]

export default function CustomerEdit() {
  const { customerId } = useParams()
  const navigate = useNavigate()

  const [customer, setCustomer] = useState(null)
  const [terminology, setTerminology] = useState({})
  // Terminology as loaded: it is edited in its own state, so a save compares against this.
  const [savedTerminology, setSavedTerminology] = useState({})
  const [platformConfig, setPlatformConfig] = useState(null)
  const [activeTab, setActiveTab] = useState('overview')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState(null)
  // A save that sends nothing says why here (nothing changed, or the server found no change).
  const [notice, setNotice] = useState(null)
  const [form, setForm] = useState({})
  // Every save says why (rule 6); the server records who and when.
  const [reason, setReason] = useState('')
  const [reasonMissing, setReasonMissing] = useState(false)
  const reasonRef = useRef(null)
  const statusRef = useRef(null)
  const [history, setHistory] = useState(null)
  const [suggestions, setSuggestions] = useState(null)
  const [reviewBusy, setReviewBusy] = useState(null)

  // Delete-customer preview/execute state -- fixed 2026-09-15 to call the real
  // purge tool (services/tenant_purge.py) instead of the older broken path.
  const [deletePreview, setDeletePreview] = useState(null)
  const [deleteBusy, setDeleteBusy] = useState(false)
  const [deleteError, setDeleteError] = useState(null)

  useEffect(() => {
    async function load() {
      try {
        const [ctx, term, config] = await Promise.allSettled([
          getCustomer(customerId),
          getTerminology(customerId),
          getPlatformConfig(),
        ])
        if (ctx.status === 'fulfilled') {
          setCustomer(ctx.value)
          setForm(ctx.value)
        }
        if (term.status === 'fulfilled') {
          setTerminology(term.value?.terminology || {})
          setSavedTerminology(term.value?.terminology || {})
        }
        if (config.status === 'fulfilled') {
          setPlatformConfig(config.value)
        }
      } catch (err) {
        console.error('Failed to load:', err)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [customerId])

  const loadHistory = useCallback(() => {
    getContextHistory(customerId)
      .then(r => setHistory(r.history || []))
      .catch(e => setHistory({ error: e.message }))
  }, [customerId])
  const loadSuggestions = useCallback(() => {
    getContextSuggestions(customerId)
      .then(r => setSuggestions(r.suggestions || []))
      .catch(e => setSuggestions({ error: e.message }))
  }, [customerId])
  useEffect(() => { loadSuggestions() }, [loadSuggestions])
  useEffect(() => { if (activeTab === 'history') loadHistory() }, [activeTab, loadHistory])

  // A save's outcome is shown above the tab; bring it into view wherever the user has scrolled.
  useEffect(() => {
    if (error || notice || saved) statusRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' })
  }, [error, notice, saved])

  const pendingSuggestions = Array.isArray(suggestions) ? suggestions.filter(s => s.status === 'pending') : []

  async function reviewSuggestion(s, decision) {
    const note = window.prompt(decision === 'approve'
      ? 'Approve this suggestion. Note (optional; it becomes the reason in the history):'
      : 'Reject this suggestion. Note (optional):', '')
    if (note === null) return
    setReviewBusy(s.id)
    setError(null)
    try {
      if (decision === 'approve') {
        await approveContextSuggestion(customerId, s.id, note)
        const fresh = await getCustomer(customerId)
        setCustomer(fresh)
        setForm(fresh)
        setTerminology(fresh.terminology || {})
        setSavedTerminology(fresh.terminology || {})
      } else {
        await rejectContextSuggestion(customerId, s.id, note)
      }
      loadSuggestions()
    } catch (err) {
      setError(err.message?.startsWith('API 409')
        ? 'This field changed after the suggestion was made. Review it against the current value.'
        : (err.message || 'Review failed'))
    } finally {
      setReviewBusy(null)
    }
  }

  // ── Derive config values from platform-config ──
  const industries = platformConfig?.industries || []
  const domains = platformConfig?.domains?.length ? platformConfig.domains : FALLBACK_DOMAINS
  const systemStatuses = platformConfig?.system_statuses?.length ? platformConfig.system_statuses : FALLBACK_STATUSES
  const contactRoles = platformConfig?.contact_roles || []
  const projectTypes = platformConfig?.project_types || []
  const projectPhases = platformConfig?.project_phases || []
  const payFreqOptions = platformConfig?.pay_frequencies || []
  const allVendors = platformConfig?.vendors || []

  // Build domain color map dynamically
  const domainColors = {}
  domains.forEach((d, i) => {
    const palette = DOMAIN_COLOR_PALETTE[i % DOMAIN_COLOR_PALETTE.length]
    domainColors[d.id] = { ...palette, label: d.label }
  })

  // Get all vendors (domain filtering will come later when vendors are tagged)
  function vendorsForDomain(domain) {
    return allVendors
  }
  function productsForVendor(vendorId) {
    const vendor = allVendors.find(v => v.label === vendorId || v.id === vendorId)
    return vendor?.products || []
  }

  // ── Field helpers ──
  function updateField(field, value) {
    setForm(prev => ({ ...prev, [field]: value }))
    setSaved(false)
    setError(null)
    setNotice(null)
  }

  function updateNested(path, value) {
    setForm(prev => {
      const copy = { ...prev }
      const keys = path.split('.')
      let obj = copy
      for (let i = 0; i < keys.length - 1; i++) {
        if (!obj[keys[i]]) obj[keys[i]] = {}
        obj[keys[i]] = { ...obj[keys[i]] }
        obj = obj[keys[i]]
      }
      obj[keys[keys.length - 1]] = value
      return copy
    })
    setSaved(false)
    setError(null)
    setNotice(null)
  }

  const unsavedFields = customer ? changedFields(customer, form, { terminology, originalTerminology: savedTerminology }) : []

  async function handleSave() {
    setError(null)
    setNotice(null)
    setSaved(false)
    const plan = savePlan({ original: customer, form, terminology, originalTerminology: savedTerminology, reason })
    if (!plan.send) {
      if (plan.kind === 'reason') {
        setError(plan.message)
        setReasonMissing(true)
        reasonRef.current?.focus()
      } else {
        setNotice(plan.message)
      }
      return
    }
    setSaving(true)
    try {
      const updated = await updateCustomer(customerId, plan.payload)
      if (!serverRecordedChange(customer, updated)) {
        setNotice(SERVER_NO_CHANGE)
        return
      }
      setCustomer(updated)
      setForm(updated)
      setTerminology(updated.terminology || {})
      setSavedTerminology(updated.terminology || {})
      setReason('')
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
      if (activeTab === 'history') loadHistory()
    } catch (err) {
      setError(err.message?.startsWith('API 409')
        ? 'Someone else changed this customer since you opened it. Reload the page and reapply your edits.'
        : (err.message || 'Save failed'))
    } finally {
      setSaving(false)
    }
  }

  // ── System helpers ──
  function updateSystem(index, field, value) {
    const systems = [...(form.systems || [])]
    systems[index] = { ...systems[index], [field]: value }

    if (field === 'domain') {
      // Domain changed — reset vendor, product, and functions (different functional areas)
      systems[index].vendor_id = ''
      systems[index].vendor_name = ''
      systems[index].product_id = ''
      systems[index].product_name = ''
      systems[index].functions = []
    }
    if (field === 'vendor_id') {
      const vendor = allVendors.find(v => v.id === value || v.label === value)
      systems[index].vendor_name = vendor?.label || value
      systems[index].product_id = ''
      systems[index].product_name = ''
      // Don't reset functions on vendor change — they're domain-level, not vendor-level
    }
    if (field === 'product_id') {
      const vendor = allVendors.find(v => v.id === systems[index].vendor_id || v.label === systems[index].vendor_id)
      const product = vendor?.products?.find(p => p.id === value || p.label === value)
      systems[index].product_name = product?.label || value
    }

    updateField('systems', systems)
  }

  function addSystem() {
    const systems = [...(form.systems || [])]
    systems.push({
      id: `sys-${Date.now()}`,
      domain: domains[0]?.id || 'hcm',
      vendor_id: '',
      vendor_name: '',
      product_id: '',
      product_name: '',
      status: 'planned',
      is_primary: systems.length === 0,
      functions: [],
      replacing_system_id: null,
    })
    updateField('systems', systems)
  }

  function deleteSystem(index) {
    updateField('systems', (form.systems || []).filter((_, i) => i !== index))
  }

  // ── Contact helpers ──
  function addContact() {
    const contacts = [...(form.contacts || [])]
    contacts.push({ id: `contact-${Date.now()}`, name: '', title: '', role: '', email: '', phone: '', notes: '' })
    updateField('contacts', contacts)
  }
  function updateContact(i, field, value) {
    const contacts = [...(form.contacts || [])]
    contacts[i] = { ...contacts[i], [field]: value }
    updateField('contacts', contacts)
  }
  function deleteContact(i) {
    updateField('contacts', (form.contacts || []).filter((_, j) => j !== i))
  }

  // ── Terminology helpers ──
  function addTerm() { setTerminology(prev => ({ ...prev, '': '' })); setNotice(null) }
  function updateTermKey(oldKey, newKey) {
    setTerminology(prev => {
      const copy = { ...prev }
      const val = copy[oldKey]
      delete copy[oldKey]
      copy[newKey] = val
      return copy
    })
  }
  function updateTermValue(key, value) { setTerminology(prev => ({ ...prev, [key]: value })) }
  function deleteTerm(key) {
    setTerminology(prev => { const copy = { ...prev }; delete copy[key]; return copy })
  }

  function deleteClarification(key) {
    const kc = { ...(form.known_clarifications || {}) }
    delete kc[key]
    updateField('known_clarifications', kc)
  }

  // ── Population helpers ──
  function addPopulation() {
    updateField('special_populations', [...(form.special_populations || []), { name: '', identifier: '', rules: '' }])
  }
  function updatePopulation(i, field, value) {
    const pops = [...(form.special_populations || [])]
    pops[i] = { ...pops[i], [field]: value }
    updateField('special_populations', pops)
  }
  function deletePopulation(i) {
    updateField('special_populations', (form.special_populations || []).filter((_, j) => j !== i))
  }

  // ── Pay group helpers ──
  function addPayGroup() {
    updateField('pay_groups', [...(form.pay_groups || []), { code: '', description: '' }])
  }
  function updatePayGroup(i, field, value) {
    const groups = [...(form.pay_groups || [])]
    groups[i] = { ...groups[i], [field]: value }
    updateField('pay_groups', groups)
  }
  function deletePayGroup(i) {
    updateField('pay_groups', (form.pay_groups || []).filter((_, j) => j !== i))
  }

  function togglePayFreq(freq) {
    const current = form.pay_frequencies || []
    const lower = freq.toLowerCase()
    const updated = current.includes(lower) ? current.filter(f => f !== lower) : [...current, lower]
    updateField('pay_frequencies', updated)
  }

  // ── Loading state ──
  if (loading) {
    return (
      <>
        <TopNav breadcrumbs={[{ label: 'Customers', href: '/customers' }, { label: 'Loading…' }]} />
        <div className="ce-loading"><div className="ce-spinner" /><p>Loading…</p></div>
      </>
    )
  }

  const companyName = form.company_name || customerId
  const breadcrumbs = [
    { label: 'Customers', href: '/customers' },
    { label: companyName, href: `/customers/${customerId}` },
    { label: 'Edit' },
  ]

  function handleCancel() {
    if (unsavedFields.length && !window.confirm('Discard your unsaved changes?')) return
    navigate(`/customers/${customerId}`)
  }

  const navActions = (
    <>
      <input
        ref={reasonRef}
        className={`ce-input ce-reason-input${reasonMissing && !reason.trim() ? ' ce-reason-missing' : ''}`}
        value={reason}
        onChange={e => { setReason(e.target.value); setReasonMissing(false) }}
        placeholder="Why are you making this change?"
        aria-label="Reason for this change"
        aria-invalid={reasonMissing && !reason.trim()}
      />
      <button className="nav-btn" onClick={handleCancel}>Cancel</button>
      <button className="nav-btn primary" onClick={handleSave} disabled={saving}>
        {saving ? 'Saving…' : 'Save Changes'}
      </button>
    </>
  )

  return (
    <>
      <TopNav breadcrumbs={breadcrumbs} actions={navActions} />

      <div className="ce-page">
        <div className="ce-tabs">
          {TABS.map(tab => (
            <button
              key={tab.id}
              className={`ce-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="ce-tab-icon">{tab.icon}</span>
              {tab.label}
              {tab.id === 'suggestions' && pendingSuggestions.length > 0 && (
                <span className="ce-tab-count">{pendingSuggestions.length}</span>
              )}
            </button>
          ))}
        </div>

        <div className="ce-content">
          <div ref={statusRef} className="ce-status">
          {saved && <div className="ce-saved-banner" role="status">✓ Saved. The change and its reason are in the History tab.</div>}
          {error && <div className="ce-error-banner" role="alert">{error}</div>}
          {notice && <div className="ce-notice-banner" role="status">{notice}</div>}
          {!saved && !notice && unsavedFields.length > 0 && (
            <div className="ce-unsaved-bar" role="status">
              Unsaved changes: {unsavedFields.join(', ').replace(/_/g, ' ')}.
              {!reason.trim() && ' Say why in the box at the top, then press Save Changes.'}
            </div>
          )}
          </div>

          {/* ════════ Tab 1: Overview ════════ */}
          {activeTab === 'overview' && (
            <div className="ce-section">
              <div className="ce-identity-header">
                <CustomerAvatar customerId={customerId} companyName={companyName} website={form.website} size="xl" />
                <div className="ce-identity-info">
                  <div className="ce-identity-name">{companyName}</div>
                  <div className="ce-identity-meta">
                    {[form.industry, form.employee_count ? `${form.employee_count.toLocaleString()} employees` : null, form.website].filter(Boolean).join(' · ')}
                  </div>
                </div>
              </div>

              <div className="ce-field-group">
                <h4 className="ce-group-title"><span className="ce-group-icon">🏢</span> Company Information</h4>
                <div className="ce-field">
                  <label className="ce-label">Company Name</label>
                  <input className="ce-input" value={form.company_name || ''} onChange={e => updateField('company_name', e.target.value)} />
                </div>
                <div className="ce-row-3">
                  <div className="ce-field">
                    <label className="ce-label">Industry</label>
                    <select className="ce-select" value={form.industry || ''} onChange={e => updateField('industry', e.target.value)}>
                      <option value="">Select…</option>
                      {industries.map(ind => <option key={ind} value={ind}>{ind}</option>)}
                    </select>
                  </div>
                  <div className="ce-field">
                    <label className="ce-label">Employee Count</label>
                    <input className="ce-input" type="number" value={form.employee_count || ''} onChange={e => updateField('employee_count', parseInt(e.target.value) || null)} />
                  </div>
                  <div className="ce-field">
                    <label className="ce-label">Website</label>
                    <input className="ce-input" value={form.website || ''} onChange={e => updateField('website', e.target.value)} placeholder="company.com" />
                  </div>
                </div>
              </div>

              <div className="ce-field-group">
                <h4 className="ce-group-title"><span className="ce-group-icon">📋</span> Project Context</h4>
                <div className="ce-row">
                  <div className="ce-field">
                    <label className="ce-label">Project Type</label>
                    <select className="ce-select" value={form.current_project_type || ''} onChange={e => updateField('current_project_type', e.target.value)}>
                      <option value="">Select…</option>
                      {projectTypes.map(t => <option key={t} value={t.toLowerCase()}>{t}</option>)}
                    </select>
                  </div>
                  <div className="ce-field">
                    <label className="ce-label">Project Phase</label>
                    <select className="ce-select" value={form.project_phase || ''} onChange={e => updateField('project_phase', e.target.value)}>
                      <option value="">Select…</option>
                      {projectPhases.map(p => <option key={p} value={p.toLowerCase()}>{p}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              <div className="ce-field-group">
                <h4 className="ce-group-title"><span className="ce-group-icon">📝</span> Consultant Notes <InternalBadge /></h4>
                <textarea className="ce-textarea" rows={6} value={form.consultant_notes || ''} onChange={e => updateField('consultant_notes', e.target.value)} placeholder="Internal notes — context, gotchas, key decisions, anything the team should know…" />
              </div>
            </div>
          )}

          {/* ════════ Tab 2: Tech Landscape ════════ */}
          {activeTab === 'systems' && (
            <div className="ce-section ce-section-wide">
              <div className="ce-section-header">
                <div>
                  <h3 className="ce-section-title">Systems Landscape</h3>
                  <p className="ce-section-desc">Configure the customer's technology stack. {allVendors.length} vendors and {platformConfig?.product_count || 0} products available.</p>
                </div>
                <button className="ce-btn-primary" onClick={addSystem}>+ Add System</button>
              </div>

              {(form.systems || []).length === 0 && (
                <div className="ce-empty-card">
                  <p>No systems configured yet.</p>
                  <button className="ce-btn-primary" onClick={addSystem}>+ Add First System</button>
                </div>
              )}

              {(form.systems || []).map((sys, i) => {
                const domainVendors = vendorsForDomain(sys.domain)
                const vendorProducts = productsForVendor(sys.vendor_id)
                const statusConfig = systemStatuses.find(s => s.id === sys.status) || systemStatuses[0]
                const domainColor = domainColors[sys.domain] || domainColors[domains[0]?.id] || { ...DOMAIN_COLOR_PALETTE[0], label: '?' }

                return (
                  <div key={sys.id || i} className={`ce-system-card ${sys.is_primary ? 'primary' : ''}`} style={{ borderLeftColor: domainColor.border }}>
                    <div className="ce-system-card-header">
                      <div className="ce-system-card-title">
                        <span className="ce-domain-badge" style={{ background: domainColor.bg, color: domainColor.text, borderColor: domainColor.border }}>{domainColor.label}</span>
                        <span className={`ce-status-dot ${statusConfig.color}`} />
                        {sys.vendor_name && sys.product_name
                          ? `${sys.vendor_name} — ${sys.product_name}`
                          : sys.vendor_name || `System ${i + 1}`}
                        {sys.is_primary && <span className="ce-primary-badge">Primary</span>}
                        {isResold(sys) && <span className="ce-via-badge">via {providerLabel(sys.provided_through, allVendors)}</span>}
                      </div>
                      <div className="ce-system-card-actions">
                        {!sys.is_primary && (
                          <button className="ce-text-btn" onClick={() => {
                            const systems = (form.systems || []).map((s, j) => ({ ...s, is_primary: j === i }))
                            updateField('systems', systems)
                          }}>Make Primary</button>
                        )}
                        <button className="ce-delete-btn" onClick={() => deleteSystem(i)}>×</button>
                      </div>
                    </div>

                    <div className="ce-system-card-body">
                      <div className="ce-row-4">
                        <div className="ce-field">
                          <label className="ce-label">Domain</label>
                          <select className="ce-select" value={sys.domain || ''} onChange={e => updateSystem(i, 'domain', e.target.value)}>
                            {domains.map(d => <option key={d.id} value={d.id}>{d.label}</option>)}
                          </select>
                        </div>
                        <div className="ce-field">
                          <label className="ce-label">Vendor</label>
                          <select className="ce-select" value={sys.vendor_id || ''} onChange={e => updateSystem(i, 'vendor_id', e.target.value)}>
                            <option value="">Select vendor…</option>
                            {domainVendors.map(v => <option key={v.id} value={v.id}>{v.label}</option>)}
                          </select>
                        </div>
                        <div className="ce-field">
                          <label className="ce-label">Product</label>
                          <select className="ce-select" value={sys.product_id || ''} onChange={e => updateSystem(i, 'product_id', e.target.value)} disabled={!sys.vendor_id}>
                            <option value="">Select product…</option>
                            {vendorProducts.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                          </select>
                        </div>
                        <div className="ce-field">
                          <label className="ce-label">Status</label>
                          <select className="ce-select" value={sys.status || 'planned'} onChange={e => updateSystem(i, 'status', e.target.value)}>
                            {systemStatuses.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                          </select>
                        </div>
                      </div>

                      {(() => {
                        const domainDef = domains.find(d => d.id === sys.domain)
                        const availableAreas = domainDef?.functional_areas || []
                        if (availableAreas.length === 0) return null
                        const selectedFns = sys.functions || []
                        return (
                          <div className="ce-functions-section">
                            <label className="ce-label">Functions In Scope</label>
                            <div className="ce-chip-grid">
                              {availableAreas.map(fa => {
                                const active = selectedFns.includes(fa)
                                return (
                                  <button
                                    key={fa}
                                    type="button"
                                    className={`ce-chip ${active ? 'active' : ''}`}
                                    onClick={() => {
                                      const updated = active
                                        ? selectedFns.filter(f => f !== fa)
                                        : [...selectedFns, fa]
                                      updateSystem(i, 'functions', updated)
                                    }}
                                  >
                                    {active && <span className="ce-chip-check">✓</span>}
                                    {fa.replace(/_/g, ' ')}
                                  </button>
                                )
                              })}
                            </div>
                          </div>
                        )
                      })()}

                      <div className="ce-row">
                        <div className="ce-field">
                          <label className="ce-label">Contracted through</label>
                          <select className="ce-select" value={isResold(sys) ? sys.provided_through : DIRECT}
                            onChange={e => updateSystem(i, 'provided_through', e.target.value)} disabled={!sys.vendor_id}>
                            {contractedThroughOptions(allVendors, sys.vendor_id).map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                          </select>
                          <div className="ce-field-hint">
                            {isResold(sys)
                              ? `No direct contract with ${sys.vendor_name || 'this vendor'}: the customer gets it through ${providerLabel(sys.provided_through, allVendors)}, so no direct connection is expected.`
                              : 'Direct: the customer contracts with this vendor.'}
                          </div>
                        </div>
                        <div className="ce-field">
                          <label className="ce-label">Notes</label>
                          <input className="ce-input" value={sys.notes || ''} onChange={e => updateSystem(i, 'notes', e.target.value)} placeholder="Version, module notes, etc." />
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {/* ════════ Tab 3: Contacts ════════ */}
          {activeTab === 'contacts' && (
            <div className="ce-section">
              <div className="ce-section-header">
                <div>
                  <h3 className="ce-section-title">Key Contacts <InternalBadge /></h3>
                  <p className="ce-section-desc">People involved in this engagement.</p>
                </div>
                <button className="ce-btn-primary" onClick={addContact}>+ Add Contact</button>
              </div>

              {(form.contacts || []).length === 0 && (
                <div className="ce-empty-card">
                  <p>No contacts added yet.</p>
                  <button className="ce-btn-primary" onClick={addContact}>+ Add First Contact</button>
                </div>
              )}

              {(form.contacts || []).map((c, i) => (
                <div key={c.id || i} className="ce-contact-card">
                  <div className="ce-contact-card-header">
                    <div className="ce-contact-avatar" style={{ background: CONTACT_AVATAR_COLORS[i % CONTACT_AVATAR_COLORS.length] }}>{(c.name || '?')[0]?.toUpperCase()}</div>
                    <div className="ce-contact-card-info">
                      <span className="ce-contact-card-name">{c.name || 'New Contact'}</span>
                      <span className="ce-contact-card-role">{contactRoles.find(r => r.id === c.role)?.label || c.role || 'No role'}</span>
                    </div>
                    <button className="ce-delete-btn" onClick={() => deleteContact(i)}>×</button>
                  </div>
                  <div className="ce-contact-card-body">
                    <div className="ce-row">
                      <div className="ce-field">
                        <label className="ce-label">Name</label>
                        <input className="ce-input" value={c.name || ''} onChange={e => updateContact(i, 'name', e.target.value)} />
                      </div>
                      <div className="ce-field">
                        <label className="ce-label">Title</label>
                        <input className="ce-input" value={c.title || ''} onChange={e => updateContact(i, 'title', e.target.value)} placeholder="e.g. VP Human Resources" />
                      </div>
                    </div>
                    <div className="ce-row-3">
                      <div className="ce-field">
                        <label className="ce-label">Role</label>
                        <select className="ce-select" value={c.role || ''} onChange={e => updateContact(i, 'role', e.target.value)}>
                          <option value="">Select…</option>
                          {contactRoles.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
                        </select>
                      </div>
                      <div className="ce-field">
                        <label className="ce-label">Email</label>
                        <input className="ce-input" type="email" value={c.email || ''} onChange={e => updateContact(i, 'email', e.target.value)} />
                      </div>
                      <div className="ce-field">
                        <label className="ce-label">Phone</label>
                        <input className="ce-input" value={c.phone || ''} onChange={e => updateContact(i, 'phone', e.target.value)} placeholder="Optional" />
                      </div>
                    </div>
                    <div className="ce-field">
                      <label className="ce-label">Notes</label>
                      <input className="ce-input" value={c.notes || ''} onChange={e => updateContact(i, 'notes', e.target.value)} placeholder="Decision authority, availability, preferences…" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ════════ Tab 4: Terminology ════════ */}
          {activeTab === 'terminology' && (
            <div className="ce-section">
              <div className="ce-section-header">
                <div>
                  <h3 className="ce-section-title">Customer Terminology</h3>
                  <p className="ce-section-desc">Map what the customer calls things to standard terms Sempirical understands. This powers automatic translation in queries and reports.</p>
                </div>
                <button className="ce-btn-primary" onClick={addTerm}>+ Add Term</button>
              </div>

              <div className="ce-term-table">
                <div className="ce-term-header">
                  <span>Their Term</span>
                  <span></span>
                  <span>Standard Term</span>
                  <span></span>
                </div>
                {Object.entries(terminology).map(([key, val], i) => (
                  <div key={i} className="ce-term-row">
                    <input className="ce-input ce-input-sm" value={key} onChange={e => updateTermKey(key, e.target.value)} placeholder="Customer's term" />
                    <span className="ce-term-arrow">→</span>
                    <input className="ce-input ce-input-sm" value={val} onChange={e => updateTermValue(key, e.target.value)} placeholder="Standard term" />
                    <button className="ce-delete-btn" onClick={() => deleteTerm(key)}>×</button>
                  </div>
                ))}
                {Object.keys(terminology).length === 0 && (
                  <div className="ce-term-empty">No terminology mappings yet. Add one to help Sempirical understand this customer's language.</div>
                )}
              </div>
            </div>
          )}

          {/* ════════ Tab 5: Populations ════════ */}
          {activeTab === 'populations' && (
            <div className="ce-section">
              <div className="ce-section-header">
                <div>
                  <h3 className="ce-section-title">Special Populations</h3>
                  <p className="ce-section-desc">Define employee groups that need special handling — unions, contractors, executives, etc. These inform compliance rules and analysis filters.</p>
                </div>
                <button className="ce-btn-primary" onClick={addPopulation}>+ Add Population</button>
              </div>

              {(form.special_populations || []).length === 0 && (
                <div className="ce-empty-card">
                  <p>No special populations defined yet.</p>
                  <button className="ce-btn-primary" onClick={addPopulation}>+ Add First Population</button>
                </div>
              )}

              {(form.special_populations || []).map((pop, i) => (
                <div key={i} className="ce-population-card">
                  <div className="ce-row-3">
                    <div className="ce-field">
                      <label className="ce-label">Name</label>
                      <input className="ce-input" value={pop.name || ''} onChange={e => updatePopulation(i, 'name', e.target.value)} placeholder="e.g. Union Hourly" />
                    </div>
                    <div className="ce-field">
                      <label className="ce-label">Identifier</label>
                      <input className="ce-input" value={pop.identifier || ''} onChange={e => updatePopulation(i, 'identifier', e.target.value)} placeholder="Column or code" />
                    </div>
                    <div className="ce-field" style={{ display: 'flex', alignItems: 'flex-end' }}>
                      <button className="ce-delete-btn" onClick={() => deletePopulation(i)}>×</button>
                    </div>
                  </div>
                  <div className="ce-field">
                    <label className="ce-label">Rules / Notes</label>
                    <input className="ce-input" value={pop.rules || ''} onChange={e => updatePopulation(i, 'rules', e.target.value)} placeholder="Special processing rules, CBA references…" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ════════ Tab 6: Data & Pay ════════ */}
          {activeTab === 'data' && (
            <div className="ce-section">
              <div className="ce-field-group">
                <h4 className="ce-group-title"><span className="ce-group-icon">📅</span> Pay Frequencies</h4>
                <p className="ce-section-desc">Select all pay frequencies this customer uses.</p>
                <div className="ce-chip-grid">
                  {payFreqOptions.map(freq => {
                    const active = (form.pay_frequencies || []).includes(freq.toLowerCase())
                    return (
                      <button key={freq} type="button" className={`ce-chip ${active ? 'active' : ''}`} onClick={() => togglePayFreq(freq)}>
                        {active && <span className="ce-chip-check">✓</span>} {freq}
                      </button>
                    )
                  })}
                </div>
              </div>

              <div className="ce-field-group">
                <div className="ce-section-header">
                  <h4 className="ce-group-title"><span className="ce-group-icon">👥</span> Pay Groups</h4>
                  <button className="ce-btn-secondary" onClick={addPayGroup}>+ Add</button>
                </div>
                {(form.pay_groups || []).length === 0 && (
                  <div className="ce-term-empty">No pay groups defined.</div>
                )}
                {(form.pay_groups || []).map((pg, i) => (
                  <div key={i} className="ce-paygroup-row">
                    <input className="ce-input ce-input-sm" value={pg.code || ''} onChange={e => updatePayGroup(i, 'code', e.target.value)} placeholder="Code (e.g. PLANT_HOURLY)" />
                    <input className="ce-input ce-input-sm" style={{ flex: 2 }} value={pg.description || ''} onChange={e => updatePayGroup(i, 'description', e.target.value)} placeholder="Description" />
                    <button className="ce-delete-btn" onClick={() => deletePayGroup(i)}>×</button>
                  </div>
                ))}
              </div>

              <div className="ce-field-group">
                <h4 className="ce-group-title"><span className="ce-group-icon">⚠️</span> Data Quality Notes <InternalBadge /></h4>
                <textarea className="ce-textarea" rows={4} value={(form.data_quality_notes || []).join('\n')} onChange={e => updateField('data_quality_notes', e.target.value.split('\n').filter(Boolean))} placeholder="One per line — known issues, gaps, workarounds…" />
              </div>
            </div>
          )}

          {/* ════════ Tab 7: Delivery ════════ */}
          {activeTab === 'delivery' && (
            <div className="ce-section">
              <div className="ce-field-group">
                <h4 className="ce-group-title"><span className="ce-group-icon">📤</span> Delivery Preferences</h4>
                <p className="ce-section-desc">How this customer prefers to receive exports and reports.</p>
                <div className="ce-row">
                  <div className="ce-field">
                    <label className="ce-label">Report Format</label>
                    <select className="ce-select" value={form.delivery_preferences?.format || 'excel'} onChange={e => updateNested('delivery_preferences.format', e.target.value)}>
                      <option value="excel">Excel (.xlsx)</option>
                      <option value="docx">Word (.docx)</option>
                      <option value="pdf">PDF</option>
                    </select>
                  </div>
                  <div className="ce-field">
                    <label className="ce-label">Detail Level</label>
                    <select className="ce-select" value={form.delivery_preferences?.detail_level || 'detailed'} onChange={e => updateNested('delivery_preferences.detail_level', e.target.value)}>
                      <option value="executive">Executive Summary</option>
                      <option value="detailed">Detailed</option>
                      <option value="technical">Technical</option>
                    </select>
                  </div>
                </div>
                <div className="ce-row-3">
                  <div className="ce-field">
                    <label className="ce-label">Currency Format</label>
                    <input className="ce-input" value={form.delivery_preferences?.currency_format || 'USD'} onChange={e => updateNested('delivery_preferences.currency_format', e.target.value)} />
                  </div>
                  <div className="ce-field">
                    <label className="ce-label">Date Format</label>
                    <input className="ce-input" value={form.delivery_preferences?.date_format || 'MM/DD/YYYY'} onChange={e => updateNested('delivery_preferences.date_format', e.target.value)} />
                  </div>
                  <div className="ce-field">
                    <label className="ce-label">Include Charts</label>
                    <select className="ce-select" value={form.delivery_preferences?.include_charts ? 'yes' : 'no'} onChange={e => updateNested('delivery_preferences.include_charts', e.target.value === 'yes')}>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="ce-field-group">
                <h4 className="ce-group-title"><span className="ce-group-icon">💡</span> Known Clarifications <InternalBadge /></h4>
                <p className="ce-section-desc">Pre-answered questions so Sempirical doesn't ask them again during analysis.</p>
                {Object.entries(form.known_clarifications || {}).map(([key, val], i) => (
                  <div key={i} className="ce-clarification-card">
                    <div className="ce-clarification-head">
                      <div className="ce-clarification-key">{key.replace(/_/g, ' ')}</div>
                      <button className="ce-delete-btn" onClick={() => deleteClarification(key)} aria-label="Delete clarification">×</button>
                    </div>
                    <textarea className="ce-textarea ce-textarea-sm" rows={2} value={val} onChange={e => {
                      const kc = { ...(form.known_clarifications || {}), [key]: e.target.value }
                      updateField('known_clarifications', kc)
                    }} />
                  </div>
                ))}
                {Object.keys(form.known_clarifications || {}).length === 0 && (
                  <div className="ce-term-empty">No clarifications recorded yet. These are added automatically during analysis.</div>
                )}
              </div>
            </div>
          )}

          {/* ════════ Suggestions: customers suggest, consultants and admins decide ════════ */}
          {activeTab === 'suggestions' && (
            <div className="ce-section">
              <div className="ce-section-header">
                <div>
                  <h3 className="ce-section-title">Suggested Changes</h3>
                  <p className="ce-section-desc">Changes the customer has asked for. Nothing changes until a consultant or admin approves; an approval is recorded in the history.</p>
                </div>
              </div>
              {suggestions?.error && <div className="ce-term-empty">Could not load suggestions: {suggestions.error}</div>}
              {Array.isArray(suggestions) && suggestions.length === 0 && (
                <div className="ce-term-empty">No suggestions yet.</div>
              )}
              {Array.isArray(suggestions) && suggestions.map(s => (
                <div key={s.id} className={`ce-suggestion-card ${s.status}`}>
                  <div className="ce-suggestion-head">
                    <span className="ce-suggestion-field">{(s.field_path || '').replace(/_/g, ' ')}</span>
                    <span className={`ce-suggestion-status ${s.status}`}>{s.status}</span>
                  </div>
                  <div className="ce-suggestion-values">
                    <div><span className="ce-label">Was</span> <code>{fmtValue(s.base_value)}</code></div>
                    <div><span className="ce-label">Proposed</span> <code>{fmtValue(s.proposed_value)}</code></div>
                  </div>
                  {s.customer_note && <div className="ce-suggestion-note">“{s.customer_note}”</div>}
                  <div className="ce-suggestion-meta">
                    {s.submitted_by_email} · {fmtWhen(s.created_at)}
                    {s.reviewer_email && <> · {s.status} by {s.reviewer_email}{s.review_note ? `: ${s.review_note}` : ''}</>}
                  </div>
                  {s.status === 'pending' && (
                    <div className="ce-suggestion-actions">
                      <button className="ce-btn-primary" disabled={reviewBusy === s.id} onClick={() => reviewSuggestion(s, 'approve')}>Approve</button>
                      <button className="ce-btn-secondary" disabled={reviewBusy === s.id} onClick={() => reviewSuggestion(s, 'reject')}>Reject</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* ════════ History: who, when, what, why ════════ */}
          {activeTab === 'history' && (
            <div className="ce-section ce-section-wide">
              <div className="ce-section-header">
                <div>
                  <h3 className="ce-section-title">Edit History <InternalBadge /></h3>
                  <p className="ce-section-desc">Every change to this customer's context: who made it (the signed-in user), when, what changed and why.</p>
                </div>
              </div>
              {history === null && <div className="ce-term-empty">Loading…</div>}
              {history?.error && <div className="ce-term-empty">Could not load history: {history.error}</div>}
              {Array.isArray(history) && history.length === 0 && <div className="ce-term-empty">No changes recorded yet.</div>}
              {Array.isArray(history) && history.length > 0 && (
                <table className="ce-history-table">
                  <thead>
                    <tr><th>When</th><th>Who</th><th>Field</th><th>From</th><th>To</th><th>Why</th></tr>
                  </thead>
                  <tbody>
                    {history.map(h => (
                      <tr key={h.id}>
                        <td>{fmtWhen(h.at)}</td>
                        <td>{h.actor_email}<div className="ce-history-sub">{h.actor_role}{h.source !== 'edit' ? ` · ${h.source}` : ''}</div></td>
                        <td>{(h.field_path || '').replace(/_/g, ' ')}{h.part === 'internal' && <div className="ce-history-sub">internal</div>}</td>
                        <td><code>{fmtValue(h.old_value)}</code></td>
                        <td><code>{fmtValue(h.new_value)}</code></td>
                        <td>{h.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* ── Danger Zone (overview tab only) ── */}
          {activeTab === 'overview' && (
          <div style={{
            marginTop: 24, padding: '20px 24px',
            border: '1px solid #fecaca', borderRadius: 10,
            background: '#fbeef0'
          }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#c0334e', marginBottom: 4 }}>Danger Zone</div>
            <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 14, lineHeight: 1.5 }}>
              Permanently delete this customer and all associated data — every Supabase table
              that carries a customer_id, and every DuckDB warehouse table. Preview first;
              nothing is deleted until you explicitly execute. This action cannot be undone.
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button
                disabled={deleteBusy}
                style={{
                  padding: '8px 20px', fontSize: 13, fontWeight: 600, borderRadius: 6,
                  border: '1px solid #c0334e', background: '#fff', color: '#c0334e',
                  cursor: deleteBusy ? 'default' : 'pointer', fontFamily: 'inherit',
                  opacity: deleteBusy ? 0.6 : 1,
                }}
                onClick={async () => {
                  setDeleteBusy(true); setDeleteError(null); setDeletePreview(null)
                  try {
                    const result = await previewDeleteCustomer(customerId)
                    setDeletePreview(result)
                  } catch (err) {
                    setDeleteError(err.message || 'Preview failed')
                  } finally {
                    setDeleteBusy(false)
                  }
                }}
              >{deleteBusy ? 'Working…' : 'Preview Delete'}</button>

              {deletePreview && (
                <button
                  disabled={deleteBusy}
                  style={{
                    padding: '8px 20px', fontSize: 13, fontWeight: 600, borderRadius: 6,
                    border: '1px solid #c0334e', background: '#c0334e', color: '#fff',
                    cursor: deleteBusy ? 'default' : 'pointer', fontFamily: 'inherit',
                    opacity: deleteBusy ? 0.6 : 1,
                  }}
                  onClick={async () => {
                    const name = form.company_name || customerId
                    if (!window.confirm(
                      `Permanently delete "${name}" (${customerId})?\n\n` +
                      `${deletePreview.supabase_row_total ?? '?'} Supabase row(s) across ` +
                      `${Object.keys(deletePreview.supabase || {}).length} table(s), ` +
                      `${deletePreview.warehouse_table_count ?? '?'} DuckDB table(s), and ` +
                      `${deletePreview.chromadb_doc_total ?? '?'} ChromaDB document(s).\n\n` +
                      `This cannot be undone.`
                    )) return
                    setDeleteBusy(true); setDeleteError(null)
                    try {
                      await executeDeleteCustomer(customerId)
                      navigate('/customers')
                    } catch (err) {
                      setDeleteError(err.message || 'Delete failed')
                      setDeleteBusy(false)
                    }
                  }}
                >Execute Delete</button>
              )}
            </div>

            {deleteError && (
              <div style={{ marginTop: 12, fontSize: 13, color: '#c0334e' }}>{deleteError}</div>
            )}

            {deletePreview && (
              <div style={{ marginTop: 14, fontSize: 12.5, color: '#4b5563', lineHeight: 1.6 }}>
                <div><strong>Supabase:</strong> {deletePreview.supabase_row_total ?? 0} row(s)
                  across {Object.keys(deletePreview.supabase || {}).length} table(s)
                  (of {deletePreview.supabase_tables_scanned ?? '?'} scanned)</div>
                <div><strong>DuckDB:</strong> {deletePreview.warehouse_table_count ?? 0} table(s)</div>
                <div><strong>ChromaDB:</strong> {deletePreview.chromadb_doc_total ?? 0} document(s)
                  across {Object.keys(deletePreview.chromadb || {}).length} collection(s)</div>
                {(deletePreview.key_type_mismatched_tables || []).length > 0 && (
                  <div style={{ marginTop: 4, color: '#c0334e' }}>
                    ⚠ {deletePreview.key_type_mismatched_tables.length} table(s) could not be
                    matched by id (uuid-keyed customer_id) — see the purge tool's own report.
                  </div>
                )}
              </div>
            )}
          </div>
          )}

        </div>
      </div>
    </>
  )
}
